import React, { useState, useEffect, useCallback } from 'react'
import { windowStickerSettingsApi, FORMAT_FIELDS, BOLD, ITALIC, UNDERLINE } from '../../services/windowStickerSettings'
import type { WindowStickerSetting } from '../../services/windowStickerSettings'
import { Toast } from '../shared/Toast'

function FormatToggle({ value, flag, label, onChange }: {
  value: number
  flag: number
  label: string
  onChange: (newValue: number) => void
}) {
  const isActive = (value & flag) !== 0
  const toggle = () => onChange(isActive ? value & ~flag : value | flag)

  return (
    <button
      onClick={toggle}
      title={label}
      className={`w-8 h-8 rounded flex items-center justify-center text-xs font-bold transition-colors ${
        isActive
          ? 'bg-secondary text-white'
          : 'bg-gray-100 text-gray-400 hover:bg-gray-200 hover:text-gray-600'
      }`}
    >
      {label}
    </button>
  )
}

export function WindowStickerSettings(): React.ReactElement | null {
  const [setting, setSetting] = useState<WindowStickerSetting | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)
  const [hasChanges, setHasChanges] = useState(false)
  const [showXslt, setShowXslt] = useState(false)

  // Local form state
  const [formLanguage, setFormLanguage] = useState('english')
  const [formXslt, setFormXslt] = useState('')
  const [formFormats, setFormFormats] = useState<Record<string, number>>({})

  const showToast = useCallback((message: string, type: 'success' | 'error') => {
    setToast({ message, type })
  }, [])

  const syncFormState = useCallback((s: WindowStickerSetting) => {
    setFormLanguage(s.default_language || 'english')
    setFormXslt(s.xslt_text || '')
    const formats: Record<string, number> = {}
    FORMAT_FIELDS.forEach(({ key }) => {
      formats[key] = (s as unknown as Record<string, number>)[key] || 0
    })
    setFormFormats(formats)
    setHasChanges(false)
  }, [])

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await windowStickerSettingsApi.show()
      setSetting(data)
      syncFormState(data)
    } catch {
      setError('Failed to load window sticker settings. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [syncFormState])

  useEffect(() => {
    loadSettings()
  }, [loadSettings])

  const markChanged = () => setHasChanges(true)

  const handleFormatChange = (key: string, value: number) => {
    setFormFormats(prev => ({ ...prev, [key]: value }))
    markChanged()
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      const params: Record<string, unknown> = {
        default_language: formLanguage,
        xslt_text: formXslt || null,
        ...formFormats,
      }
      const updated = await windowStickerSettingsApi.update(params)
      setSetting(updated)
      syncFormState(updated)
      showToast('Window sticker settings saved', 'success')
    } catch {
      showToast('Failed to save settings', 'error')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-12">
        <div className="flex items-center justify-center gap-3 text-grey">
          <div className="w-5 h-5 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
          <span className="text-sm">Loading window sticker settings...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
        <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center mx-auto mb-4">
          <svg className="w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
        </div>
        <p className="text-sm text-grey mb-4">{error}</p>
        <button onClick={loadSettings} className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90">
          Try Again
        </button>
      </div>
    )
  }

  if (!setting) return null

  return (
    <div className="space-y-6">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-secondary">Window Stickers</h1>
          <p className="text-sm text-grey mt-1">Configure formatting and templates for vehicle window stickers.</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving || !hasChanges}
          className="inline-flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {saving && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* Language Selection */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-secondary">Default Language</h2>
          <p className="text-xs text-grey mt-0.5">Choose the default language for generated window stickers.</p>
        </div>
        <div className="p-6">
          <select
            value={formLanguage}
            onChange={(e) => { setFormLanguage(e.target.value); markChanged() }}
            aria-label="Default Language"
            className="w-full max-w-xs px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none bg-white"
          >
            <option value="english">English</option>
            <option value="spanish">Spanish</option>
          </select>
        </div>
      </div>

      {/* Format Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-secondary">Field Formatting</h2>
          <p className="text-xs text-grey mt-0.5">Set bold, italic, and underline styles for each window sticker field.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-left text-xs font-medium text-grey uppercase tracking-wider px-6 py-3">Field</th>
                <th className="text-center text-xs font-medium text-grey uppercase tracking-wider px-3 py-3 w-12">B</th>
                <th className="text-center text-xs font-medium text-grey uppercase tracking-wider px-3 py-3 w-12">I</th>
                <th className="text-center text-xs font-medium text-grey uppercase tracking-wider px-3 py-3 w-12">U</th>
                <th className="text-left text-xs font-medium text-grey uppercase tracking-wider px-6 py-3">Preview</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {FORMAT_FIELDS.map(({ key, label }) => {
                const val = formFormats[key] || 0
                const isBold = (val & BOLD) !== 0
                const isItalic = (val & ITALIC) !== 0
                const isUnderline = (val & UNDERLINE) !== 0

                return (
                  <tr key={key} className="hover:bg-gray-50/50">
                    <td className="px-6 py-3 text-sm text-secondary">{label}</td>
                    <td className="px-3 py-3 text-center">
                      <FormatToggle value={val} flag={BOLD} label="B" onChange={(v) => handleFormatChange(key, v)} />
                    </td>
                    <td className="px-3 py-3 text-center">
                      <FormatToggle value={val} flag={ITALIC} label="I" onChange={(v) => handleFormatChange(key, v)} />
                    </td>
                    <td className="px-3 py-3 text-center">
                      <FormatToggle value={val} flag={UNDERLINE} label="U" onChange={(v) => handleFormatChange(key, v)} />
                    </td>
                    <td className="px-6 py-3">
                      <span
                        className="text-sm text-grey"
                        style={{
                          fontWeight: isBold ? 'bold' : 'normal',
                          fontStyle: isItalic ? 'italic' : 'normal',
                          textDecoration: isUnderline ? 'underline' : 'none',
                        }}
                      >
                        Sample Text
                      </span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Advanced: XSLT Template */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <button
          onClick={() => setShowXslt(!showXslt)}
          className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="text-left">
            <h2 className="text-sm font-semibold text-secondary">Advanced: XSLT Template</h2>
            <p className="text-xs text-grey mt-0.5">Custom XSLT for window sticker PDF generation.</p>
          </div>
          <svg
            className={`w-5 h-5 text-grey transition-transform ${showXslt ? 'rotate-180' : ''}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
          </svg>
        </button>
        {showXslt && (
          <div className="px-6 pb-6 border-t border-gray-100 pt-4">
            <label htmlFor="xslt-template" className="block text-xs font-medium text-grey mb-2">
              XSLT Template (leave empty to use default)
            </label>
            <textarea
              id="xslt-template"
              value={formXslt}
              onChange={(e) => { setFormXslt(e.target.value); markChanged() }}
              rows={12}
              placeholder="Paste your custom XSLT template here..."
              className="w-full px-3 py-2 text-sm font-mono border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none resize-y bg-gray-50"
            />
          </div>
        )}
      </div>
    </div>
  )
}
